import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NodeExecutionStatus } from 'types';

import clsxm from '@/lib/clsxm';
import { getNodeCreditCost } from '@/lib/workflows/executor';
import { useWorkflowStore } from '@/lib/workflows/store';

import Button from '@/components/buttons/Button';
import EditModal from '@/components/images/EditModal';
import { SimpleTooltip } from '@/components/ui/tooltip';

import { isGptImage2FamilyModel } from '@/constants/gptImageModels';
import { modelsWithDescription } from '@/constants/imageModels';

import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { NodeActionsDropdown } from './NodeActionsDropdown';
import { TipTapPromptEditor } from '../TipTapPromptEditor';

// Edit models from EditModal
export const EDIT_MODELS = [
  {
    value: 'Nano Banana 2 Lite',
    label: 'Nano Banana 2 Lite',
    credits: 1,
    resolutions: ['1K'],
  },
  {
    value: 'Nano Banana 2',
    label: 'Nano Banana 2',
    credits: 2,
    resolutions: ['1K', '2K', '4K'],
  },
  {
    value: 'Nano Banana Pro',
    label: 'Nano Banana Pro',
    credits: 4,
    resolutions: ['2K', '4K', '1K'],
  },
  // {
  //   value: 'Nano Banana Flash',
  //   label: 'Nano Banana Flash',
  //   credits: 1,
  //   resolutions: ['2K', '4K', '1K'],
  // },
  {
    value: 'Seedream 4.5',
    label: 'Seedream 4.5',
    credits: 2,
    resolutions: ['1K', '2K', '4K'],
  },
  { value: 'OpenAI GPT Image 1.5', label: 'GPT Image 1.5', credits: 8 },
  {
    value: 'OpenAI GPT Image 2 Low',
    label: 'GPT Image 2 Low',
    credits: 0.5,
  },
  {
    value: 'OpenAI GPT Image 2 Medium',
    label: 'GPT Image 2 Medium',
    credits: 3,
  },
  {
    value: 'OpenAI GPT Image 2 High',
    label: 'GPT Image 2 High',
    credits: 11,
  },
  {
    value: 'OpenAI GPT Image 2.5 Flare Low',
    label: 'GPT Image 2.5 Flare Low',
    credits: 0.5,
  },
  {
    value: 'OpenAI GPT Image 2.5 Flare Medium',
    label: 'GPT Image 2.5 Flare Medium',
    credits: 3,
  },
  {
    value: 'OpenAI GPT Image 2.5 Flare High',
    label: 'GPT Image 2.5 Flare High',
    credits: 11,
  },
  {
    value: 'OpenAI GPT Image 2.5 Sunburst Medium',
    label: 'GPT Image 2.5 Sunburst Medium',
    credits: 3,
  },
  {
    value: 'OpenAI GPT Image 2.5 Sunburst High',
    label: 'GPT Image 2.5 Sunburst High',
    credits: 11,
  },
  {
    value: 'FLUX.2 [max]',
    label: 'FLUX.2 [max]',
    credits: 2.5,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'FLUX.2 [pro]',
    label: 'FLUX.2 [pro]',
    credits: 2,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'FLUX.2 [flex]',
    label: 'FLUX.2 [flex]',
    credits: 3,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'Seedream 4.0',
    label: 'Seedream 4.0',
    credits: 2,
    resolutions: ['1K', '2K', '4K'],
  },
  { value: 'Nano Banana', label: 'Nano Banana', credits: 2 },
  { value: 'Qwen Image', label: 'Qwen Image', credits: 2 },
  { value: 'Reve', label: 'Reve', credits: 2 },
  { value: 'Reve Fast', label: 'Reve Fast', credits: 0.5 },
  { value: 'Flux Kontext Pro', label: 'Flux Kontext Pro', credits: 2 },
  { value: 'Flux Kontext Max', label: 'Flux Kontext Max', credits: 4 },
  { value: 'OpenAI GPT Image 1', label: 'GPT Image 1', credits: 8 },
  {
    value: 'Ideogram v3 Character',
    label: 'Ideogram v3 Character',
    credits: 5,
  },
  {
    value: 'Grok',
    label: 'Grok',
    resolutions: ['1K', '2K'],
    credits: 2,
  },
  {
    value: 'Grok Pro',
    label: 'Grok Pro',
    resolutions: ['1K', '2K'],
    credits: 4,
  },
  {
    value: 'Grok Imagine 2.0',
    label: 'Grok Imagine 2.0',
    resolutions: ['1K', '2K'],
    credits: 4,
  },
  {
    value: 'Muse Image',
    label: 'Muse Image',
    credits: 1,
  },
];

interface ImageEditConfig {
  editModel: string;
  dimensions: string;
  prompt: string;
  useHD: boolean;
  resolution: string;
}

interface ImageEditNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
  readOnly?: boolean;
}

// Get dimension options based on edit model
function getDimensionsForEditModel(
  editModel: string,
): Array<{ value: string; label: string }> {
  if (
    editModel === 'Nano Banana 2' ||
    editModel === 'Nano Banana 2 Lite' ||
    editModel === 'Nano Banana Pro'
  ) {
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1:1', label: 'Square (1:1)' },
      { value: '2:3', label: 'Portrait (2:3)' },
      { value: '3:2', label: 'Landscape (3:2)' },
      { value: '3:4', label: 'Portrait (3:4)' },
      { value: '4:3', label: 'Landscape (4:3)' },
      { value: '4:5', label: 'Portrait (4:5)' },
      { value: '5:4', label: 'Landscape (5:4)' },
      { value: '9:16', label: 'Portrait (9:16)' },
      { value: '16:9', label: 'Landscape (16:9)' },
      { value: '21:9', label: 'Ultrawide (21:9)' },
    ];
  } else if (editModel === 'Nano Banana') {
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1:1', label: 'Square (1:1)' },
      { value: '2:3', label: 'Portrait (2:3)' },
      { value: '3:2', label: 'Landscape (3:2)' },
      { value: '3:4', label: 'Portrait (3:4)' },
      { value: '4:3', label: 'Landscape (4:3)' },
      { value: '4:5', label: 'Portrait (4:5)' },
      { value: '5:4', label: 'Landscape (5:4)' },
      { value: '9:16', label: 'Portrait (9:16)' },
      { value: '16:9', label: 'Landscape (16:9)' },
      { value: '21:9', label: 'Ultrawide (21:9)' },
    ];
  } else if (
    editModel === 'Qwen Image' ||
    editModel === 'FLUX.2 [max]' ||
    editModel === 'FLUX.2 [pro]' ||
    editModel === 'FLUX.2 [flex]' ||
    editModel === 'Seedream 4.5' ||
    editModel === 'Seedream 4.0' ||
    editModel === 'Ideogram v3 Character'
  ) {
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1:1', label: 'Square (1:1)' },
      { value: '3:4', label: 'Portrait (3:4)' },
      { value: '4:3', label: 'Landscape (4:3)' },
      { value: '9:16', label: 'Portrait (9:16)' },
      { value: '16:9', label: 'Landscape (16:9)' },
    ];
  } else if (
    editModel === 'OpenAI GPT Image 1' ||
    editModel === 'OpenAI GPT Image 1.5'
  ) {
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1024x1024', label: 'Square (1024x1024)' },
      { value: '1536x1024', label: 'Landscape (1536x1024)' },
      { value: '1024x1536', label: 'Portrait (1024x1536)' },
    ];
  } else if (isGptImage2FamilyModel(editModel)) {
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1:1', label: 'Square (1:1)' },
      { value: '2:3', label: 'Portrait (2:3)' },
      { value: '3:2', label: 'Landscape (3:2)' },
      { value: '9:16', label: 'Portrait (9:16)' },
      { value: '16:9', label: 'Landscape (16:9)' },
    ];
  } else if (
    editModel === 'Grok' ||
    editModel === 'Grok Pro' ||
    editModel === 'Grok Imagine 2.0' ||
    editModel === 'Muse Image'
  ) {
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1:1', label: 'Square (1:1)' },
      { value: '3:4', label: 'Portrait (3:4)' },
      { value: '4:3', label: 'Landscape (4:3)' },
      { value: '9:16', label: 'Portrait (9:16)' },
      { value: '16:9', label: 'Landscape (16:9)' },
      { value: '2:3', label: 'Portrait (2:3)' },
      { value: '3:2', label: 'Landscape (3:2)' },
    ];
  } else if (editModel === 'Reve Fast' || editModel === 'Reve') {
    return [{ value: 'auto', label: 'Auto' }];
  } else {
    // Flux Kontext Pro/Max - full options
    return [
      { value: 'auto', label: 'Auto (Original)' },
      { value: '1:1', label: 'Square (1:1)' },
      { value: '21:9', label: 'Ultrawide (21:9)' },
      { value: '16:9', label: 'Landscape (16:9)' },
      { value: '4:3', label: 'Landscape (4:3)' },
      { value: '3:2', label: 'Landscape (3:2)' },
      { value: '2:3', label: 'Portrait (2:3)' },
      { value: '3:4', label: 'Portrait (3:4)' },
      { value: '9:16', label: 'Portrait (9:16)' },
      { value: '9:21', label: 'Portrait Ultrawide (9:21)' },
    ];
  }
}

export function ImageEditNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
  readOnly,
}: ImageEditNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState(!data.result);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const executionStatus =
    (data.status as NodeExecutionStatus | undefined) || 'idle';
  const isRunning = executionStatus === 'running';

  const config: ImageEditConfig = {
    editModel: (data.config as ImageEditConfig)?.editModel || 'Nano Banana Pro',
    dimensions: (data.config as ImageEditConfig)?.dimensions || 'auto',
    resolution: (data.config as ImageEditConfig)?.resolution || '2K',
    prompt: (data.config as ImageEditConfig)?.prompt || '',
    useHD: (data.config as ImageEditConfig)?.useHD || false,
  };

  // Track previous result to detect when it changes
  const prevResultRef = useRef(data.result);

  // Auto-collapse settings when result is generated (only when result changes)
  useEffect(() => {
    const resultChanged = prevResultRef.current !== data.result;
    if (resultChanged && data.result && showSettings && !isRunning) {
      setShowSettings(false);
    }
    prevResultRef.current = data.result;
  }, [data.result, isRunning, showSettings]);

  // Auto-expand settings when running
  useEffect(() => {
    if (isRunning && !showSettings) {
      setShowSettings(true);
    }
  }, [isRunning, showSettings]);

  // Hide result if running
  const shouldShowResult = !!data.result && !isRunning;

  // Get valid dimensions for the current edit model
  const availableDimensions = useMemo(() => {
    return getDimensionsForEditModel(config.editModel);
  }, [config.editModel]);

  const availableResolutions = useMemo(() => {
    return (
      EDIT_MODELS.find((model) => model.value === config.editModel)
        ?.resolutions || []
    );
  }, [config.editModel]);

  // Reset dimensions if current selection is invalid for the model
  useEffect(() => {
    const dimensionValues = availableDimensions.map((d) => d.value);
    if (
      availableDimensions.length > 0 &&
      !dimensionValues.includes(config.dimensions)
    ) {
      updateNodeData(id, {
        config: {
          ...config,
          dimensions: availableDimensions[0].value,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.editModel]);

  // Reset resolution when switching to a model with different resolution options
  useEffect(() => {
    if (
      availableResolutions.length > 0 &&
      !availableResolutions.includes(config.resolution)
    ) {
      updateNodeData(id, {
        config: {
          ...config,
          resolution: availableResolutions[0],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.editModel, availableResolutions]);

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = e.target.value;
    const newDimensions = getDimensionsForEditModel(newModel);
    const newResolutions =
      EDIT_MODELS.find((model) => model.value === newModel)?.resolutions || [];

    updateNodeData(id, {
      config: {
        ...config,
        editModel: newModel,
        // Reset to first available dimension for new model
        dimensions: newDimensions[0].value,
        resolution:
          newResolutions.length > 0
            ? newResolutions.includes(config.resolution)
              ? config.resolution
              : newResolutions[0]
            : config.resolution,
      },
    });
  };

  const handleDimensionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        dimensions: e.target.value,
      },
    });
  };

  const handleResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        resolution: e.target.value,
      },
    });
  };

  const handlePromptChange = (value: string) => {
    updateNodeData(id, {
      config: {
        ...config,
        prompt: value,
      },
    });
  };

  const handleHDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        useHD: e.target.checked,
      },
    });
  };

  // Connected text nodes for mention autocomplete
  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) =>
        edge.target === id &&
        (edge.targetHandle === 'prompt' || edge.targetHandle === 'text'),
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        const baseLabel = (sourceNode?.data?.label as string) || 'Node';
        return {
          id: sourceNode?.id || '',
          label: baseLabel,
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  // Get model icon from constants
  const getModelIcon = useMemo(() => {
    const modelData = modelsWithDescription.find(
      (m) => m.title === config.editModel,
    );

    if (modelData?.icon) {
      const IconComponent = modelData.icon;
      return <IconComponent className='w-4 h-4' />;
    }

    // Fallback to default icon if model not found
    return (
      <svg
        className='w-4 h-4 text-blue-600'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
      </svg>
    );
  }, [config.editModel]);

  // Calculate credit cost for this node
  const creditCost = useMemo(() => {
    const mockNode = {
      id,
      type: 'image-edit',
      position: { x: 0, y: 0 },
      data: { config },
    };
    return getNodeCreditCost(mockNode);
  }, [id, config]);

  // Extract image URL for dropdown
  const imageUrl = useMemo(() => {
    if (typeof data.result === 'string') {
      return data.result;
    }
    if (Array.isArray(data.result) && data.result.length > 0) {
      return data.result[0];
    }
    return undefined;
  }, [data.result]);

  return (
    <>
      <BaseNode
        title={(data.label as string) || t('Image to Image')}
        icon={getModelIcon}
        hasInput={false}
        hasOutput={false}
        status={executionStatus}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        readOnly={readOnly}
        disableMinWidth={!showSettings && !!data.result}
        customInputs={
          <React.Fragment>
            {createInputHandle('image', 'image', 30)}
            {createInputHandle('text', 'prompt', 70)}
          </React.Fragment>
        }
        customOutputs={createOutputHandle('image', 'image', 50)}
        hideToolbarActions
        customHeaderButtons={
          <>
            {(data as any)?.result && !isRunning && (
              <SimpleTooltip
                title={showSettings ? t('Hide settings') : t('Show settings')}
                side='top'
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings(!showSettings);
                  }}
                  className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
                >
                  <svg
                    className='w-5 h-5 text-gray-600 dark:text-gray-300'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                </button>
              </SimpleTooltip>
            )}
            <NodeActionsDropdown
              nodeId={nodeId || id}
              nodeTitle={(data.label as string) || t('Image to Image')}
              mediaUrl={imageUrl}
              mediaType='image'
              filename='edited-image.png'
              readOnly={readOnly}
              onEditImage={imageUrl ? () => setShowEditModal(true) : undefined}
            />
          </>
        }
      >
        <div className={showSettings || !data.result ? 'w-[400px]' : ''}>
          {/* Collapsible Settings Section */}
          <div
            className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
              showSettings ? '' : 'hidden'
            }`}
          >
            {/* Model Selection */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Edit Model')}
              </label>
              <select
                value={config.editModel}
                onChange={handleModelChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                disabled={readOnly}
              >
                {EDIT_MODELS.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Dimensions */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Dimensions')}
              </label>
              <select
                value={config.dimensions}
                onChange={handleDimensionsChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                disabled={readOnly}
              >
                {availableDimensions.map((dim) => (
                  <option key={dim.value} value={dim.value}>
                    {dim.label}
                  </option>
                ))}
              </select>
            </div>

            {availableResolutions.length > 0 && (
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Resolution')}
                </label>
                <select
                  value={config.resolution}
                  onChange={handleResolutionChange}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  disabled={readOnly}
                >
                  {availableResolutions.map((resolution) => (
                    <option key={resolution} value={resolution}>
                      {resolution}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Prompt */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Instructions')}
              </label>
              <TipTapPromptEditor
                value={config.prompt || ''}
                onChange={readOnly ? () => {} : handlePromptChange}
                connectedNodes={getConnectedTextNodes()}
                placeholder={t(
                  'Describe the changes... Type @ to reference connected nodes',
                )}
              />
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                {t('Type @ to reference connected nodes')}
              </p>
            </div>

            {/* HD Quality */}
            {/* <div className='flex items-center'>
            <input
              type='checkbox'
              id={`hd-${id}`}
              checked={config.useHD}
              onChange={handleHDChange}
              className='h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700'
              disabled={readOnly}
            />
            <label
              htmlFor={`hd-${id}`}
              className='ml-2 text-xs text-gray-700 dark:text-gray-300'
            >
              Use HD quality
            </label>
          </div> */}

            {/* Run Button (inside settings) */}
            <div className='pt-1'>
              <Button
                className='w-full justify-center'
                variant='outline'
                isLoading={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode?.(nodeId || id);
                }}
              >
                {data.result ? (
                  <>
                    <RefreshIcon className='w-4 h-4 mr-2' />
                    <span>
                      {t('Regenerate')} ({creditCost}{' '}
                      {creditCost === 1 ? t('credit') : t('credits')})
                    </span>
                  </>
                ) : (
                  <>
                    <PlayIcon className='w-4 h-4 mr-2' />
                    <span>
                      {t('Run')} ({creditCost}{' '}
                      {creditCost === 1 ? t('credit') : t('credits')})
                    </span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Result Preview - Prominent Display */}
          {shouldShowResult && (
            <div
              className={clsxm(
                showSettings
                  ? 'w-full px-4 pb-3'
                  : isLandscape
                    ? 'w-[700px]'
                    : 'w-[420px]',
              )}
            >
              {typeof data.result === 'string' ? (
                <div className='relative group rounded-md overflow-hidden'>
                  <img
                    src={data.result}
                    alt='Edited'
                    className='w-full h-auto object-cover'
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      setIsLandscape(img.naturalWidth > img.naturalHeight);
                    }}
                  />
                  <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                    <button
                      onClick={() =>
                        window.open(data.result as string, '_blank')
                      }
                      className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                      title={t('Open in new tab')}
                    >
                      <svg
                        className='w-4 h-4 text-gray-700'
                        fill='none'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : Array.isArray(data.result) ? (
                <div className='grid grid-cols-2 gap-3'>
                  {data.result.slice(0, 4).map((url, i) => (
                    <div
                      key={i}
                      className='relative group rounded-md overflow-hidden'
                    >
                      <img
                        src={url}
                        alt={`Edited ${i + 1}`}
                        className='w-full h-40 object-cover'
                      />
                      <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                        <button
                          onClick={() => window.open(url, '_blank')}
                          className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                          title={t('Open in new tab')}
                        >
                          <svg
                            className='w-4 h-4 text-gray-700'
                            fill='none'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {/* Error Display */}
          {!!data.error && (
            <div className='mx-3 mb-3 border-t border-red-200 bg-red-50 px-2 py-2 rounded'>
              <p className='text-xs text-red-600'>{String(data.error)}</p>
            </div>
          )}
        </div>
      </BaseNode>
      {imageUrl && (
        <EditModal
          image={{ image_url: imageUrl }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedImage) => {
            if (updatedImage.image_url) {
              updateNodeData(id, { result: updatedImage.image_url });
            }
          }}
        />
      )}
    </>
  );
}
